import { Divider, Stack, Typography, useTheme } from "@mui/material";
import { type FC, useState } from "react";
import {
	type ClientPropertyEstimatedPriceHistoryResponseDTO,
	ClientPropertyTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	DataCard,
	DataCardPreset,
} from "web_myplann/Shared/Components/DataCard";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import {
	dateFormatter,
	moneyFormatter,
} from "web_myplann/Shared/Utils/formatters";

const PageResources = Resources.PortfolioDetail.Property;

type Props = {
	reproductionValue: number | null | undefined;
	interiorPrice: number | null | undefined;
	marketValue: number | null | undefined;
	priceHistory: ClientPropertyEstimatedPriceHistoryResponseDTO[];
	currencySign: string;
	type:
		| ClientPropertyTypeEnum.House
		| ClientPropertyTypeEnum.Flat
		| ClientPropertyTypeEnum.Cottage
		| ClientPropertyTypeEnum.Land;
};

export const PropertyDataCard: FC<Props> = ({
	reproductionValue,
	interiorPrice,
	marketValue,
	priceHistory,
	currencySign,
	type,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	const [isHistoryShown, setIsHistoryShown] = useState(false);

	const sortedPriceHistory = priceHistory?.sort(
		(a, b) =>
			new Date(b.estimatedPriceAt).getTime() -
			new Date(a.estimatedPriceAt).getTime(),
	);

	return (
		<>
			<DataCard preset={DataCardPreset.PropertyValue}>
				<Stack>
					<DataRow
						label={t(PageResources.MarketPrice)}
						onLabelTooltipClick={
							marketValue ? () => setIsHistoryShown(true) : undefined
						}
						value={moneyFormatter(marketValue, currencySign)}
					/>
					<DataRow
						label={t(PageResources.ReproductionPrice)}
						value={moneyFormatter(reproductionValue, currencySign)}
					/>
					<DataRow
						label={
							type === ClientPropertyTypeEnum.Land
								? t(PageResources.LandPrice)
								: t(PageResources.InteriorPrice)
						}
						value={moneyFormatter(interiorPrice, currencySign)}
					/>
				</Stack>
			</DataCard>

			<MyPlannDrawer
				open={isHistoryShown}
				onClose={() => setIsHistoryShown(false)}
				title={t(Resources.PortfolioDetail.Property.MarketPriceHistory)}
			>
				<Stack direction="column" p={1.6} gap={1.2}>
					{sortedPriceHistory?.map((item, index) => (
						<Stack key={item.id} direction="column" gap={1.2}>
							<Typography
								color={theme.palette.deprecated.grey[700]}
								fontWeight={600}
							>
								{`${t(Resources.Common.ToDate)} ${dateFormatter(item.estimatedPriceAt)}`}
							</Typography>

							{item.estimatedPriceMin && (
								<Stack direction="row" justifyContent={"space-between"}>
									<Typography color={theme.palette.deprecated.grey[500]}>
										{t(Resources.Common.Min)}
									</Typography>
									<Typography
										color={theme.palette.deprecated.grey[700]}
										fontWeight={600}
									>
										{moneyFormatter(item.estimatedPriceMin, currencySign)}
									</Typography>
								</Stack>
							)}

							{item.estimatedPriceMax && (
								<Stack direction="row" justifyContent={"space-between"}>
									<Typography color={theme.palette.deprecated.grey[500]}>
										{t(Resources.Common.Max)}
									</Typography>
									<Typography
										color={theme.palette.deprecated.grey[700]}
										fontWeight={600}
									>
										{moneyFormatter(item.estimatedPriceMax, currencySign)}
									</Typography>
								</Stack>
							)}

							{index < priceHistory.length - 1 && <Divider />}
						</Stack>
					))}
				</Stack>
			</MyPlannDrawer>
		</>
	);
};
